import React, { useEffect, useState, useCallback } from 'react';
import { fetchChores, fetchRoster, completeChore, resetChore, updateChore } from '../api';

const ROOM_ORDER = [
  'Kitchen', 'Dining Room', 'Living Room',
  'Upstairs Bathroom', 'Downstairs Bathroom',
];

export default function Chores() {
  const [chores, setChores]   = useState([]);
  const [roster, setRoster]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [c, r] = await Promise.all([fetchChores(), fetchRoster()]);
    setChores(c);
    setRoster(r);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 60 * 1000);
    // Reload at midnight so display resets and misses get logged
    const now = new Date();
    const msUntilMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1) - now;
    const midnight = setTimeout(() => { load(); }, msUntilMidnight);
    return () => { clearInterval(poll); clearTimeout(midnight); };
  }, [load]);

  const handleToggleDone = async (chore) => {
    if (chore.done_today) {
      await resetChore(chore.id);
      setChores(prev => prev.map(c => c.id === chore.id
        ? { ...c, done_today: false, completed_by_name: null, completed_by_color: null }
        : c
      ));
    } else {
      // Complete with assigned person if set, otherwise no one
      const completedBy = chore.assigned_to || null;
      const updated = await completeChore(chore.id, completedBy);
      setChores(prev => prev.map(c => c.id === chore.id ? { ...c, ...updated } : c));
    }
  };

  const handleAssign = async (choreId, rosterId) => {
    await updateChore(choreId, { assigned_to: rosterId || null });
    const member = roster.find(r => r.id === parseInt(rosterId));
    setChores(prev => prev.map(c => c.id === choreId
      ? { ...c, assigned_to: rosterId || null, assignee_name: member?.name || null, assignee_color: member?.color || null }
      : c
    ));
  };

  // Group by room
  const grouped = ROOM_ORDER.reduce((acc, room) => {
    const roomChores = chores.filter(c => c.room === room);
    if (roomChores.length) acc[room] = roomChores;
    return acc;
  }, {});

  const totalDone = chores.filter(c => c.done_today).length;

  if (loading) return <div style={cs.root}><p style={cs.empty}>Loading…</p></div>;

  return (
    <div style={cs.root}>
      {/* Summary bar */}
      <div style={cs.summary}>
        <span style={cs.panelLabel}>Chores</span>
        <span style={cs.dot} />
        <span style={cs.summaryText}>
          {totalDone} / {chores.length} done today
        </span>
      </div>

      <div style={cs.roomList}>
        {Object.entries(grouped).map(([room, roomChores]) => {
          const roomDone = roomChores.filter(c => c.done_today).length;
          const allDone  = roomDone === roomChores.length;

          return (
            <div key={room} style={cs.roomGroup}>
              {/* Room heading */}
              <div style={cs.roomHeader}>
                <span style={{ ...cs.roomName, ...(allDone ? cs.roomNameDone : {}) }}>
                  {room}
                </span>
                <span style={cs.roomProgress}>
                  {roomDone}/{roomChores.length}
                </span>
                {allDone && <span style={cs.roomCheck}>✓</span>}
              </div>

              {/* Chore rows */}
              {roomChores.map(chore => (
                <div key={chore.id} style={{
                  ...cs.choreRow,
                  ...(chore.done_today ? cs.choreRowDone : cs.choreRowPending),
                }}>

                  {/* Status toggle */}
                  <button
                    style={{
                      ...cs.statusBtn,
                      ...(chore.done_today ? cs.statusBtnDone : cs.statusBtnPending),
                    }}
                    onClick={() => handleToggleDone(chore)}
                    title={chore.done_today ? 'Mark undone' : 'Mark done'}
                  >
                    {chore.done_today ? '✓' : '○'}
                  </button>

                  {/* Chore info */}
                  <div style={cs.choreInfo}>
                    <span style={{
                      ...cs.choreName,
                      ...(chore.done_today ? cs.choreNameDone : {}),
                    }}>
                      {chore.name}
                    </span>
                    <span style={cs.choreFreq}>{chore.frequency}</span>
                  </div>

                  {/* Assignee selector */}
                  <select
                    style={{
                      ...cs.assignSelect,
                      color: chore.assignee_color || 'var(--text-dim)',
                      borderColor: chore.assignee_color
                        ? chore.assignee_color + '66'
                        : 'var(--border)',
                    }}
                    value={chore.assigned_to || ''}
                    onChange={e => handleAssign(chore.id, e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {roster.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>

                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const cs = {
  root: {
    display: 'flex', flexDirection: 'column',
    height: '100%', overflow: 'hidden', gap: 12,
  },
  summary: {
    display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
  },
  panelLabel: {
    fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
    letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-dimmer)',
  },
  dot: {
    width: 6, height: 6, borderRadius: '50%',
    background: 'var(--accent2)', boxShadow: '0 0 6px var(--accent2)',
  },
  summaryText: {
    fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--text-dim)',
  },
  roomList: {
    display: 'flex', flexDirection: 'column', gap: 16,
    overflowY: 'auto', flex: 1,
  },
  roomGroup: {
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  roomHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '0 4px 6px',
    borderBottom: '1px solid var(--border)',
    marginBottom: 2,
  },
  roomName: {
    fontFamily: 'var(--font-mono)', fontSize: '0.62rem',
    letterSpacing: '0.14em', textTransform: 'uppercase',
    color: 'var(--text-dim)', flex: 1,
  },
  roomNameDone: { color: 'var(--accent3)' },
  roomProgress: {
    fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-dimmer)',
  },
  roomCheck: { color: 'var(--accent3)', fontSize: '0.75rem' },
  choreRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 10px', borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    transition: 'all 0.2s',
  },
  choreRowPending: {
    background: 'var(--surface2)',
  },
  choreRowDone: {
    background: 'rgba(122,232,184,0.04)',
    border: '1px solid rgba(122,232,184,0.2)',
    opacity: 0.65,
  },
  statusBtn: {
    width: 28, height: 28, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-mono)', fontSize: '0.8rem',
    cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
    border: 'none',
  },
  statusBtnPending: {
    background: 'transparent',
    border: '2px solid var(--border)',
    color: 'var(--text-dimmer)',
  },
  statusBtnDone: {
    background: 'rgba(122,232,184,0.15)',
    border: '2px solid var(--accent3)',
    color: 'var(--accent3)',
  },
  choreInfo: {
    display: 'flex', flexDirection: 'column', gap: 2, flex: 1,
  },
  choreName: {
    fontSize: '0.88rem', color: 'var(--text)',
  },
  choreNameDone: {
    textDecoration: 'line-through', color: 'var(--text-dimmer)',
  },
  choreFreq: {
    fontFamily: 'var(--font-mono)', fontSize: '0.58rem',
    color: 'var(--text-dimmer)', textTransform: 'uppercase', letterSpacing: '0.08em',
  },
  assignSelect: {
    fontFamily: 'var(--font-mono)', fontSize: '0.68rem',
    background: 'var(--bg)', borderRadius: 8,
    color: 'var(--text-dim)', padding: '4px 8px',
    cursor: 'pointer', flexShrink: 0, border: '1px solid var(--border)',
    outline: 'none', maxWidth: 110,
  },
  empty: {
    fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
    color: 'var(--text-dimmer)', fontStyle: 'italic',
  },
};
